import { useEffect, useState } from "react";
import * as styleVars from "../../theme/exports.scss";
import { isBrowser, useWindowDimensions } from "@travellocal/ui";

const breakpoints = {
  tablet: parseInt(styleVars.tabletWidth),
  desktop: parseInt(styleVars.desktopWidth),
  widescreen: parseInt(styleVars.widescreenWidth),
  fullhd: parseInt(styleVars.fullhdWidth),
};

type RenderFunc = () => JSX.Element;
type BreakpointRenderFuncs = { [TKey in keyof typeof breakpoints]: RenderFunc };

interface OwnProps extends Partial<BreakpointRenderFuncs> {
  /**
   * The component to render below tablet breakpoint, and if no other render funcs are defined.
   */
  mobile: RenderFunc;
}

/**
 * Server-side-rendering-safe way to render different components at different breakpoints.
 */
const ResponsiveContainer = ({ mobile, tablet, desktop, widescreen, fullhd }: OwnProps) => {
  const viewport = useWindowDimensions();
  const [isMounted, setMounted] = useState(false);
  useEffect(() => setMounted(isBrowser()), []);

  // First render (whether server or browser) should always be the same -
  // we're using mobile layout as mobiles are generally less capable than desktops
  if (!isMounted || viewport.width < breakpoints.tablet) {
    // NB. mobile() will be called twice even on mobile
    // - React doesn't have a key it can use to prevent re-render
    return mobile();
  }
  else if (viewport.width < breakpoints.desktop) {
    return (tablet ?? mobile)();
  }
  else if (viewport.width < breakpoints.widescreen) {
    return (desktop ?? tablet ?? mobile)();
  }
  else if (viewport.width < breakpoints.fullhd) {
    return (widescreen ?? desktop ?? tablet ?? mobile)();
  }
  else {
    return (fullhd ?? widescreen ?? desktop ?? tablet ?? mobile)();
  }
};

export default ResponsiveContainer;
